/**
 * Test Status Enum
 * Maps to backend TestStatus enum values (stored as lowercase snake_case strings in DB)
 */
export const TestStatus = {
  Created: 'created',
  BaselineStarted: 'baseline_started',
  BaselineCompleted: 'baseline_completed',
  SpecimenStarted: 'specimen_started',
  SpecimenCompleted: 'specimen_completed',
  Classifying: 'classifying',
  Complete: 'complete',
  Failed: 'failed',
  Cancelled: 'cancelled',
  ClassificationFailed: 'classification_failed'
} as const;

export type TestStatus = (typeof TestStatus)[keyof typeof TestStatus];

/**
 * Classification Status Enum
 * Tracks the asynchronous classification workflow separately from test status
 */
export const ClassificationStatus = {
  ClassificationPending: 'classification_pending',
  ClassificationStarted: 'classification_started',
  Classified: 'classified',
  Failed: 'failed'
} as const;

export type ClassificationStatus = (typeof ClassificationStatus)[keyof typeof ClassificationStatus];

/**
 * Human-readable labels for test statuses
 */
export const TestStatusLabels: Record<TestStatus, string> = {
  [TestStatus.Created]: 'Created',
  [TestStatus.BaselineStarted]: 'Baseline Started',
  [TestStatus.BaselineCompleted]: 'Baseline Complete',
  [TestStatus.SpecimenStarted]: 'Specimen Started',
  [TestStatus.SpecimenCompleted]: 'Specimen Complete',
  [TestStatus.Classifying]: 'Classifying',
  [TestStatus.Complete]: 'Complete',
  [TestStatus.Failed]: 'Failed',
  [TestStatus.Cancelled]: 'Cancelled',
  [TestStatus.ClassificationFailed]: 'Not Classified'
};

/**
 * Colors for test status badges (Material-UI Chip colors)
 * Aligned with Dashboard status display for consistency
 */
export const TestStatusColors: Record<TestStatus, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  [TestStatus.Created]: 'info',
  [TestStatus.BaselineStarted]: 'info',
  [TestStatus.BaselineCompleted]: 'info',
  [TestStatus.SpecimenStarted]: 'info',
  [TestStatus.SpecimenCompleted]: 'info',
  [TestStatus.Classifying]: 'info',
  [TestStatus.Complete]: 'success',
  [TestStatus.Failed]: 'error',
  [TestStatus.Cancelled]: 'error',
  [TestStatus.ClassificationFailed]: 'warning'
};

/**
 * Human-readable labels for classification statuses
 */
export const ClassificationStatusLabels: Record<ClassificationStatus, string> = {
  [ClassificationStatus.ClassificationPending]: 'Pending',
  [ClassificationStatus.ClassificationStarted]: 'Processing',
  [ClassificationStatus.Classified]: 'Classified',
  [ClassificationStatus.Failed]: 'Failed'
};

/**
 * Colors for classification status badges
 */
export const ClassificationStatusColors: Record<ClassificationStatus, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  [ClassificationStatus.ClassificationPending]: 'default',
  [ClassificationStatus.ClassificationStarted]: 'info',
  [ClassificationStatus.Classified]: 'success',
  [ClassificationStatus.Failed]: 'error'
};

/**
 * Helper to parse string status to TestStatus enum
 */
export function parseTestStatus(status: string): TestStatus {
  // Map legacy statuses to new enum values
  const statusMap: Record<string, TestStatus> = {
    'running': TestStatus.BaselineStarted,
    'sampled': TestStatus.SpecimenCompleted,
    'classified': TestStatus.Complete
  };

  const normalized = status.toLowerCase();
  return (statusMap[normalized] || normalized) as TestStatus;
}

/**
 * Helper to parse string status to ClassificationStatus enum
 */
export function parseClassificationStatus(status: string): ClassificationStatus {
  return status.toLowerCase() as ClassificationStatus;
}

/**
 * Survey data for clinical trial context
 * Stored in test.additional_info as JSON
 */
export interface SurveyData {
  ageBand?: 'child' | 'young-adult' | 'adult' | 'senior';
  biologicalSex?: 'male' | 'female';
  ethnicity?: string;
  symptomPresence?: 'yes' | 'no';
  symptomDuration?: '<24h' | '1-3days' | '4-7days' | '>7days';
  priorTestResult?: 'positive' | 'negative' | 'none';
  geographicRegion?: string;
}